// CBaseResultScene — 牌局結束三畫面共用基底
// 對應反編譯原版 BaseGameResultScene（保留資料流，背景採本專案綠金牌桌）。
//
// 三個結算 scene（CResultTileScene / CResultPointScene / CResultMoneyScene）都繼承本類別，
// 共用：
//   1. 綠金牌桌背景（createBackdrop）
//   2. payload 解析（resolveFlow）：正式對局收 TResultFlowData；預覽 / 無 payload 時注入 mock
//   3. 玩家 header 渲染（renderPlayerHeader）：頭像 + 座位字 + 風字 + 莊標 + 名字
//
// === 使用圖片 ===
// - 背景共用 createTableBackground（Phaser.Graphics）；舊 result-table.png 預留但目前未採用。
// - public/assets/ui/result-avatar.png（key: RESULT_AVATAR_KEY，1×2）— 玩家頭像
// - public/assets/ui/result-seat-text.png（key: RESULT_SEAT_TEXT_KEY，2×2）— 自家/下家/對家/上家
// - public/assets/ui/result-wind.png（key: RESULT_WIND_KEY，2×2）— 東南西北大風字
// - public/assets/ui/icon-dealer.png（key: ICON_DEALER_KEY）— 莊家標
// - 街機按鈕透過 createPageButton / dialog-skin 呈現，詳見各檔頭；文字使用 Phaser.Text。

import Phaser from 'phaser';
import { stopBackgroundMusic } from '@audio/scene-audio';

import { seatToRelative } from '@core/types';
import { attachPressFeedback, FONT_STYLES } from '@ui/index';
import { createTableBackground } from '@ui/table-background';
import { createPageButton, PAGE_THEME } from '@ui/page-skin';
import type { CDialogButtonSurface } from '@ui/dialog-skin';

import {
  ICON_DEALER_KEY,
  RESULT_AVATAR_KEY,
  RESULT_BTN_CONTINUE_KEY,
  RESULT_SEAT_TEXT_KEY,
  RESULT_WIND_KEY,
  ResultAvatarFrame,
  ResultWindFrame,
} from './boot-scene';
import {
  buildMockResultFlowData,
  type TResultFlowData,
  type TResultPlayerInfo,
} from './result-flow-data';

// ============================================================================
// 共用版面常數
// ============================================================================
const RESULT_BUTTON_SURFACE_DATA_KEY = 'result-button-surface'; // 鍵盤焦點街機表面
const RESULT_BUTTON_ACTION_DATA_KEY = 'result-action';

// ---- 玩家 header（頭像 + 座位字 + 風字 + 莊標 + 名字）----
const HEADER_AVATAR_SIZE = 63; // 頭像原生邊長
const HEADER_SEAT_W = 126; // 座位字原生寬
const HEADER_WIND_SIZE = 63; // 風字原生邊長
const HEADER_GAP = 14; // 元素之間水平間距
const HEADER_NAME_GAP = 18; // 風字與名字之間的距離
const HEADER_NAME_FONT_SIZE = '26px'; // 名字字體
const HEADER_DEALER_SCALE = 0.62; // 莊標縮放（疊在頭像左上）
const HEADER_DEALER_OFFSET = 6; // 莊標距頭像左上角內縮

// ---- 共用街機操作鈕 ----
const RESULT_BTN_WIDTH = 320; // 按鈕寬
const RESULT_BTN_HEIGHT = 72; // 按鈕高
const RESULT_BTN_PRESS_SCALE = 0.98; // 按下回饋

/** renderPlayerHeader 回傳：整個 header 的右緣 X（讓呼叫端接著排手牌）。 */
export type THeaderLayout = {
  readonly rightX: number;
  readonly objects: ReadonlyArray<Phaser.GameObjects.GameObject>;
};

export abstract class CBaseResultScene extends Phaser.Scene {
  protected flow!: TResultFlowData;

  /** 解析 payload：有就用，沒有（預覽 / 直接進場，Phaser 會傳 `{}`）就注入固定 mock。 */
  protected resolveFlow(data?: TResultFlowData): void {
    this.flow = data && Array.isArray(data.players) ? data : buildMockResultFlowData();
  }

  /** 結算三畫面共用背景：與主遊戲相同的綠金桌面。 */
  protected createBackdrop(): void {
    stopBackgroundMusic(this);
    createTableBackground(this);
  }

  /**
   * 渲染一家的 header：頭像 + 座位字 + 風字 +（莊標）+ 名字，水平排列。
   * @param scale header 整體縮放（不同 scene 大小不同）
   */
  protected renderPlayerHeader(
    x: number,
    y: number,
    player: TResultPlayerInfo,
    scale: number,
    opts: { showName?: boolean } = {}
  ): THeaderLayout {
    const objects: Phaser.GameObjects.GameObject[] = [];
    const gap = HEADER_GAP * scale;
    let cursorX = x;

    // 頭像（origin 左中）
    const avatar = this.add
      .image(cursorX, y, RESULT_AVATAR_KEY, ResultAvatarFrame.Yellow)
      .setOrigin(0, 0.5)
      .setScale(scale);
    objects.push(avatar);

    // 莊標（疊頭像左上角）
    if (player.isDealer) {
      const dealer = this.add
        .image(
          cursorX + HEADER_DEALER_OFFSET * scale,
          y - (HEADER_AVATAR_SIZE / 2 - HEADER_DEALER_OFFSET) * scale,
          ICON_DEALER_KEY
        )
        .setOrigin(0.5, 0.5)
        .setScale(HEADER_DEALER_SCALE * scale);
      objects.push(dealer);
    }
    cursorX += HEADER_AVATAR_SIZE * scale + gap;

    // 座位字（自家/下家/對家/上家 — 依相對方位取 frame）
    const relFrame = seatToRelative(player.seat, this.flow.playerSeat);
    const seatText = this.add
      .image(cursorX, y, RESULT_SEAT_TEXT_KEY, relFrame)
      .setOrigin(0, 0.5)
      .setScale(scale);
    objects.push(seatText);
    cursorX += HEADER_SEAT_W * scale + gap;

    // 風字
    const wind = this.add
      .image(cursorX, y, RESULT_WIND_KEY, ResultWindFrame[player.wind])
      .setOrigin(0, 0.5)
      .setScale(scale);
    objects.push(wind);
    cursorX += HEADER_WIND_SIZE * scale;

    // 名字
    if (opts.showName !== false) {
      cursorX += HEADER_NAME_GAP * scale;
      const name = this.add
        .text(cursorX, y, player.name, {
          ...FONT_STYLES.body,
          fontFamily: PAGE_THEME.font,
          color: PAGE_THEME.text,
          fontSize: HEADER_NAME_FONT_SIZE,
        })
        .setOrigin(0, 0.5);
      objects.push(name);
      cursorX += name.width;
    }

    return { rightX: cursorX, objects };
  }

  /**
   * 街機大按鈕（街機表面 + 暖白字），含按壓回饋。Point / Money 共用。
   * @param textKey 按鈕文字圖 key（如 RESULT_BTN_CONTINUE_KEY）
   */
  protected createResultButton(
    x: number,
    y: number,
    textKey: string,
    onClick: () => void,
    opts: { width?: number; height?: number } = {}
  ): Phaser.GameObjects.Container {
    const w = opts.width ?? RESULT_BTN_WIDTH;
    const h = opts.height ?? RESULT_BTN_HEIGHT;

    const surface = createPageButton(
      this,
      0,
      0,
      w,
      h,
      textKey === RESULT_BTN_CONTINUE_KEY ? '繼續' : '離開'
    );
    surface.setPrimary(textKey === RESULT_BTN_CONTINUE_KEY);
    const container = this.add.container(x, y, [surface]);
    container.setSize(w, h).setInteractive({ useHandCursor: true });
    surface.bindInteraction(container);
    container.setData(RESULT_BUTTON_SURFACE_DATA_KEY, surface);
    container.setData(RESULT_BUTTON_ACTION_DATA_KEY, onClick);
    attachPressFeedback(container, container, { pressScale: RESULT_BTN_PRESS_SCALE, onClick });
    return container;
  }

  /** 鍵盤與滑鼠共用綠金面板與立體按鈕焦點。 */
  protected setResultButtonFocused(button: Phaser.GameObjects.Container, focused: boolean): void {
    const surface = button.getData(RESULT_BUTTON_SURFACE_DATA_KEY) as
      CDialogButtonSurface | undefined;
    surface?.setFocused(focused);
  }

  /** 讓 Enter／Space 與指標放開走同一個按鈕 action。 */
  protected activateResultButton(button: Phaser.GameObjects.Container): void {
    const action = button.getData(RESULT_BUTTON_ACTION_DATA_KEY) as (() => void) | undefined;
    action?.();
  }
}
